import { useMemo } from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import Icon from "core/icon/Icon";
import LoaderV2 from "core/loader/LoaderV2";
import { Typography } from "core/typography/Typography";
import { QUERY_STATUS } from "constants/constants";
import {
  DELIVERY_NOTE_GENERATE_LABELS,
  DELIVERY_NOTE_PARTIES_DETAIL,
  DELIVERY_NOTE_TEMPLATE_MESSAGES,
} from "constants/forms";
import { sortTemplatesByFavorite } from "utils/deliveryNoteHelper";

const ITEM_CLASS = "delivery-note-generate-templates__item";

const DeliveryNoteTemplatePicker = ({
  templates,
  hasActiveFilters,
  loadStatus,
  selectedTemplateId,
  onSelect,
}) => {
  const orderedTemplates = useMemo(
    () => sortTemplatesByFavorite(templates),
    [templates]
  );

  const itemClass = (template) =>
    [
      ITEM_CLASS,
      template.is_favorite ? `${ITEM_CLASS}--favorite` : "",
      template.id === selectedTemplateId ? `${ITEM_CLASS}--selected` : "",
    ]
      .filter(Boolean)
      .join(" ");

  return (
    <section
      className="delivery-note-generate-templates"
      id="delivery-note-generate-templates"
    >
      <Typography
        className="title-3 violet text-uppercase"
        type="h2"
        label={
          <FormattedMessage {...DELIVERY_NOTE_GENERATE_LABELS.chooseTemplate} />
        }
      />

      {loadStatus === QUERY_STATUS.LOADING && (
        <LoaderV2 id="delivery-note-generate-templates-loader" />
      )}

      {loadStatus === QUERY_STATUS.ERROR && (
        <Typography
          className="text-3 alizarin-crimson"
          type="p"
          label={
            <FormattedMessage
              id="delivery_note_templates_load_error"
              defaultMessage="Les modèles n'ont pas pu être chargés."
            />
          }
        />
      )}

      {loadStatus === QUERY_STATUS.SUCCESS && !orderedTemplates.length && (
        <Typography
          className="text-3 violet"
          type="p"
          label={
            <FormattedMessage
              {...(hasActiveFilters
                ? DELIVERY_NOTE_TEMPLATE_MESSAGES.noMatch
                : DELIVERY_NOTE_GENERATE_LABELS.noTemplate)}
            />
          }
        />
      )}

      <ul className="delivery-note-generate-templates__list">
        {orderedTemplates.map((template) => (
          <li className={itemClass(template)} key={template.id}>
            <button
              type="button"
              className="delivery-note-generate-templates__card"
              onClick={() => onSelect(template)}
              data-testid={`delivery-note-generate-template-card-${template.id}`}
            >
              {template.is_favorite && (
                <span className="delivery-note-generate-templates__star">
                  <Icon icon="star2" color="gamboge" />
                </span>
              )}
              <span className="delivery-note-generate-templates__identity">
                <Typography
                  className="title-5 deep-purple"
                  type="span"
                  label={template.name}
                />
                <Typography
                  className="text-3 violet"
                  type="span"
                  label={
                    <FormattedMessage
                      {...DELIVERY_NOTE_PARTIES_DETAIL}
                      values={{
                        issuer: template.issuer?.company_name || "—",
                        recipient: template.recipient?.company_name || "—",
                      }}
                    />
                  }
                />
              </span>
            </button>
            {template.id === selectedTemplateId && (
              <Typography
                className="text-3 delivery-note-generate-templates__selected"
                type="span"
                label={
                  <FormattedMessage
                    {...DELIVERY_NOTE_GENERATE_LABELS.selected}
                  />
                }
              />
            )}
          </li>
        ))}
      </ul>
    </section>
  );
};

DeliveryNoteTemplatePicker.propTypes = {
  templates: PropTypes.array,
  hasActiveFilters: PropTypes.bool,
  loadStatus: PropTypes.string,
  selectedTemplateId: PropTypes.number,
  onSelect: PropTypes.func.isRequired,
};

export default DeliveryNoteTemplatePicker;
